// Shared lobby WebSocket singleton.
//
// Both MultiplayerLobby (lobby-phase chat + ready-up + start) and
// useMultiplayerSync (in-game position + enemy + bullet sync) need to
// talk to the same `/api/ws/lobby/{id}/{wallet}` endpoint. Previously
// they each opened their own raw WebSocket, which:
//   1. Doubled the number of sockets per browser tab.
//   2. Caused 2–3 reconnect cycles during the lobby → game handoff
//      because the lobby component unmounted (closing its socket) right
//      before the game component mounted (opening a new one).
//
// This module centralises the lifetime: one WebSocket per
// (lobbyId, walletAddress) key, ref-counted across subscribers. When the
// last subscriber unsubscribes the socket is closed after a 1-second
// grace period — so the screen transition can hand off to the next
// subscriber without a reconnect.
//
// API:
//   const sub = getLobbySocket(lobbyId, walletAddress);
//   const unsub = sub.subscribe(handler);
//   sub.send({ type: 'chat', message: 'hi' });
//   sub.isConnected();
//   // on unmount:
//   unsub();
//
// `handler(data)` receives every parsed JSON message from the server.
// Each subscriber is independent — adding/removing one does NOT affect
// the others, and identical messages are delivered to all subscribers.

const RECONNECT_BACKOFF_MS = 3000;
const CLOSE_GRACE_MS = 1000;

const _sockets = new Map(); // key -> entry

function _keyOf(lobbyId, walletAddress) {
    return `${lobbyId}|${walletAddress}`;
}

function _ensureEntry(key, wsUrl, lobbyId, walletAddress) {
    let entry = _sockets.get(key);
    if (entry) return entry;

    entry = {
        ws: null,
        wsUrl,
        lobbyId,
        walletAddress,
        handlers: new Set(),
        connected: false,
        closing: false,        // true while in the grace period before close
        closeTimer: null,
        reconnectTimer: null,
    };
    _sockets.set(key, entry);
    return entry;
}

function _broadcast(entry, message) {
    // Snapshot handlers so a subscriber unsubscribing during dispatch
    // doesn't mutate the iterator we're walking.
    [...entry.handlers].forEach((h) => {
        try { h(message); }
        catch (e) { console.error('[lobbySocket] handler threw:', e); }
    });
}

function _connect(entry) {
    if (entry.ws && (entry.ws.readyState === WebSocket.OPEN || entry.ws.readyState === WebSocket.CONNECTING)) {
        return;
    }
    const url = `${entry.wsUrl}/api/ws/lobby/${entry.lobbyId}/${entry.walletAddress}`;
    const ws = new WebSocket(url);
    entry.ws = ws;
    entry.connected = false;

    ws.onopen = () => {
        entry.connected = true;
        console.info(`[lobbySocket] connected ${entry.lobbyId}/${entry.walletAddress.slice(0, 8)} (subs=${entry.handlers.size})`);
        _broadcast(entry, { type: '__socket_open__' });
    };

    ws.onmessage = (ev) => {
        let data;
        try { data = JSON.parse(ev.data); }
        catch (e) { console.error('[lobbySocket] parse failed:', e); return; }
        _broadcast(entry, data);
    };

    ws.onclose = () => {
        entry.connected = false;
        _broadcast(entry, { type: '__socket_close__' });
        // Only auto-reconnect if there are still subscribers and we're
        // not in the deliberate-close grace period.
        if (entry.handlers.size > 0 && !entry.closing) {
            if (entry.reconnectTimer) clearTimeout(entry.reconnectTimer);
            entry.reconnectTimer = setTimeout(() => _connect(entry), RECONNECT_BACKOFF_MS);
        }
    };

    ws.onerror = (err) => {
        console.warn('[lobbySocket] error:', err?.message || err);
    };
}

/**
 * Get the shared lobby socket manager for a (lobbyId, walletAddress) tuple.
 * Lazily opens the underlying WebSocket on the first `subscribe()` call.
 *
 * @param {string} lobbyId
 * @param {string} walletAddress
 * @param {string} wsUrl - absolute origin (e.g. 'wss://api.example.com')
 */
export function getLobbySocket(lobbyId, walletAddress, wsUrl) {
    const key = _keyOf(lobbyId, walletAddress);

    return {
        /**
         * Register a message handler. Returns an unsubscribe function.
         * The underlying WebSocket opens on the first subscribe, and
         * closes 1s after the LAST unsubscribe (giving a re-mount in the
         * same tick the chance to inherit the live connection).
         */
        subscribe(handler) {
            const entry = _ensureEntry(key, wsUrl, lobbyId, walletAddress);
            entry.handlers.add(handler);

            // Cancel a pending close if a new subscriber arrived in time.
            if (entry.closeTimer) {
                clearTimeout(entry.closeTimer);
                entry.closeTimer = null;
                entry.closing = false;
            }

            // Connect lazily on first subscriber.
            if (!entry.ws) {
                _connect(entry);
            } else if (entry.connected) {
                // Late subscriber — synthesize an "already open" event so
                // they can immediately send their initial messages.
                queueMicrotask(() => handler({ type: '__socket_open__' }));
            }

            return () => {
                if (!entry.handlers.has(handler)) return;
                entry.handlers.delete(handler);
                if (entry.handlers.size === 0) {
                    // No subscribers left — start a grace-period close so
                    // a screen handoff in the same tick can keep it alive.
                    if (entry.closeTimer) clearTimeout(entry.closeTimer);
                    entry.closing = true;
                    entry.closeTimer = setTimeout(() => {
                        if (entry.handlers.size > 0) return; // someone re-subbed
                        if (entry.reconnectTimer) {
                            clearTimeout(entry.reconnectTimer);
                            entry.reconnectTimer = null;
                        }
                        if (entry.ws) {
                            try { entry.ws.close(); } catch (_) { /* noop */ }
                        }
                        _sockets.delete(key);
                    }, CLOSE_GRACE_MS);
                }
            };
        },

        /** Send a JSON-encoded message. No-op if not yet connected. */
        send(message) {
            const entry = _sockets.get(key);
            if (!entry || !entry.ws) return false;
            if (entry.ws.readyState !== WebSocket.OPEN) return false;
            try {
                entry.ws.send(typeof message === 'string' ? message : JSON.stringify(message));
                return true;
            } catch (e) {
                console.error('[lobbySocket] send failed:', e);
                return false;
            }
        },

        isConnected() {
            const entry = _sockets.get(key);
            return !!(entry && entry.connected);
        },

        /** For debugging / unit tests only. */
        _debug() {
            const entry = _sockets.get(key);
            return entry ? {
                connected: entry.connected,
                handlers: entry.handlers.size,
                closing: entry.closing,
                readyState: entry.ws?.readyState,
            } : null;
        },
    };
}
